import { callable } from '@tencent/tiny-ioc';
import { ClientVarsService } from '../../../core/services/client-vars-service';
import { SlideInitialDataService } from '../../../core/services/slide-initial-data-service';
import { SlideDataServices } from '../../../core/slide-data-services.core';
import { loadOnlineClientVars } from '../loaders/online-client-vars-loader';
import { OnlineEditServices } from '../../online/core/online-edit-services.core';
import { DocxOnlineEditDataFetcher } from '../services/online-edit-data-fetcher';
import { DocxOnlineAsyncDataFetcher } from '../services/online-async-data-fetcher';
import { OnlineEditService } from '../services/online-edit-service';
import { OnlineSlideAppService } from '../services/slide-app-service.online';
import { OnlineDocInfoManager } from '../managers/online-doc-info-manager';
import { SlideTitleManagerV3 } from '../managers/slide-title-manager.v3';
import CursorManager from '../managers/cursor-manager';
import { UserManager } from '../managers/user-manager';
import CollaboratorUserManager from '../managers/collaborator-user-manager';
import CooperationLockManager from '../managers/cooperation-lock-manager';
import { CollaborationManager } from '@tencent/slide-sdk-model/src/manager/collaboration-manager';
import { OnlineApiService } from '../services/online-api-service';
import { OnlineEditNoticeManager } from '../managers/online-edit-notice-manager';
import { CommandManager } from '@tencent/slide-sdk-model';
import { isOfflineEditEnable } from '../../offline/utils';
import { registerOfflineSlideDataServices } from '../../offline/entry/offline.entry';
import { OfflineEditServices } from '../../offline/core/offline-edit-service.core';
import { shouldDisableSocket } from '@src/slide/infrastructure/module/dependent/socket';
import { OnlineEditReporter } from '../services/online-edit-reporter';
import { getQuery } from 'sutils';

const enableImportChunk = !!(getQuery('notAlreadyCreate') && Number(getQuery('notAlreadyCreate')) === 1);

export function registerOnlineSlideDataServices() {
    // 移动App和桌面App
    if (isOfflineEditEnable()) {
        registerOfflineSlideDataServices();
    }
    SlideDataServices.ClientVarsService.register(async () => {
        const clientVars = await loadOnlineClientVars();

        const initialAttributedText = clientVars.collab_client_vars?.initialAttributedText;
        const isOldPresentationData = !initialAttributedText?.slide;

        if (isOldPresentationData) {
            throw Error('can not open doc');
        } else {
            SlideDataServices.SlideInitialDataService.register(async () => {
                const OnlineEditService = await OnlineEditServices.OnlineEditService.promise();
                const initialData = await OnlineEditService.loadDocument();
                return new SlideInitialDataService(initialData);
            });
        }

        return new ClientVarsService(clientVars);
    });

    SlideDataServices.SDKDataServices.CommandManager.register((...params) => new CommandManager(
        ...params,
        () => OnlineEditServices.OTTransformManager(),
    ));
    SlideDataServices.DocInfoManager.register(callable(OnlineDocInfoManager));
    OnlineEditServices.OnlineApiService.register(callable(OnlineApiService));

    OnlineEditServices.OnlineEditNoticeManager.register(callable(OnlineEditNoticeManager));
    SlideDataServices.SlideTitleManager.register(async (...deps) => {
        const presentationStoreService = await SlideDataServices.SDKDataServices.PresentationStoreService.promise();
        const eventManager = await SlideDataServices.SDKDataServices.EventManager.promise();
        return new SlideTitleManagerV3(...deps, presentationStoreService, eventManager);
    });
    SlideDataServices.ClientVarsService.promise().then(() => {
        OnlineEditServices.OnlineEditService.register(callable(OnlineEditService));
        if (enableImportChunk) {
            OnlineEditServices.OnlineEditDataFetcher.register(callable(DocxOnlineAsyncDataFetcher));
        } else {
            OnlineEditServices.OnlineEditDataFetcher.register(callable(DocxOnlineEditDataFetcher));
        }
        OnlineEditServices.CursorManager.register(callable(CursorManager));
        OnlineEditServices.UserManager.register(callable(UserManager));
        OnlineEditServices.CollaboratorUserManager.register(callable(CollaboratorUserManager));
        OnlineEditServices.CooperationLockManager.register(callable(CooperationLockManager));
        SlideDataServices.SlideAppService.register(callable(OnlineSlideAppService));
        if (shouldDisableSocket()) {
            return;
        }
        import(/* webpackChunkName: "slide-sdk-ot-transform" */ '@tencent/slide-sdk-ot-transform')
            .then(({ OTTransform }) => SlideDataServices.SlideAppService.promise()
                .then(() =>  OnlineEditServices.OTTransformManager.register(callable(OTTransform))));

        const getCacheMutations = () => {
            try {
                const mutations = SlideDataServices.SDKDataServices.CommandManager()?.getUndoCommandListData()
                    .map(({ redoCommand }) => redoCommand.mutations);
                return mutations ?? [];
            } catch (err) {
                return [];
            }
        };
        // 移动App和桌面App
        if (isOfflineEditEnable()) {
            OnlineEditServices.CollaborationManager.register((...params) => new CollaborationManager(
                ...params,
                { getCacheMutations },
                { getOfflineEditService: () => OfflineEditServices.OfflineEditService.promise() },
            ));
        } else {
            OnlineEditServices.CollaborationManager.register((...params) => new CollaborationManager(
                ...params,
                { getCacheMutations },
            ));
        }
        return;
    });
    OnlineEditServices.OnlineEditReporter.register(callable(OnlineEditReporter));
}
